/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  ValidationRule,
  // ValidatorFunction
} from './types'

// Validadores básicos predefinidos
export const validators = {
  /**
   * Valida que un campo sea requerido (no vacío)
   *
   * @example Uso básico
   * ```typescript
   * // Validación básica de campo requerido
   * const validator = useValidator({
   *   form: {
   *     nombre: validators.requerido
   *   },
   *   refs: { form }
   * })
   * ```
   */
  requerido: (value: any): boolean | string => {
    // Validar string vacío, null, undefined o array vacío
    if (value === null || value === undefined) return 'Este campo es requerido'
    if (typeof value === 'string' && value.trim() === '') return 'Este campo es requerido'
    if (Array.isArray(value) && value.length === 0) return 'Este campo es requerido'
    if (typeof value === 'object' && Object.keys(value).length === 0) return 'Este campo es requerido'

    return true
  },

  /**
   * Valida formato de correo electrónico
   */
  correo: (value: string): boolean | string => {
    if (!value) return true // No validar si está vacío

    const patron = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return patron.test(value) || 'Correo electrónico inválido'
  },

  /**
   * Valida que un campo sea numérico
   */
  numerico: (value: any): boolean | string => {
    if (!value && value !== 0) return true // No validar si está vacío

    return !isNaN(Number(value)) || 'Debe ser un número'
  },

  /**
   * Valida la longitud mínima y máxima de un texto
   */
  longitud: (min: number, max?: number): ValidationRule => ({
    validator: (value: string) => {
      if (!value) return true // No validar si está vacío

      if (max) {
        return (value.length >= min && value.length <= max) || false
      }
      return (value.length >= min) || false
    },
    message: max
      ? `Debe tener entre ${min} y ${max} caracteres`
      : `Debe tener al menos ${min} caracteres`
  }),

  /**
   * Validación condicional que solo aplica cuando se cumple una condición
   */
  requiredIf: (condition: () => boolean): ValidationRule => ({
    validator: (value: any) => {
      if (!condition()) return true
      return !!value || false
    },
    message: 'Este campo es requerido',
    dependsOn: condition
  }),

  /**
   * Validación para seleccionar al menos un elemento en un array
   */
  arrayNoVacio: (value: any[]): boolean | string => {
    return (Array.isArray(value) && value.length > 0) || 'Debe seleccionar al menos un elemento'
  },

  /**
   * Valida que un valor sea exactamente igual al valor esperado
   */
  equals: (expectedValue: any, message?: string): ValidationRule => ({
    validator: (value: any) => {
      return value === expectedValue || (message || `Este campo debe ser ${expectedValue}`);
    }
  }),

  /**
   * Valida que una contraseña cumpla los requisitos de seguridad:
   * - Mínimo 6 caracteres
   * - Al menos una letra minúscula
   * - Al menos una letra mayúscula
   * - Al menos un número
   * - Al menos un carácter especial
   */
  segura: (value: string): boolean | string => {
    if (!value) return 'La contraseña es requerida'
    if (value.length < 6) return 'Mínimo 6 caracteres'
    if (!/[a-z]/.test(value)) return 'Debe contener una letra minúscula'
    if (!/[A-Z]/.test(value)) return 'Debe contener una letra mayúscula'
    if (!/\d/.test(value)) return 'Debe contener números'
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Debe contener caracteres especiales'
    return true
  },

  /**
   * Valida que la confirmación de contraseña coincida con la original
   * @param getPassword Función que devuelve el valor de la contraseña original
   */
  confirmaPassword: (getPassword: () => string | undefined): ValidationRule => ({
    validator: (value: any) => {
      if (!value) return 'Confirmación de contraseña requerida'
      const password = getPassword()
      if (!password) return 'La contraseña es requerida'
      const passwordValid = validators.segura(password)
      if (passwordValid !== true) return 'Corrija primero la contraseña'
      return value === password || 'Las contraseñas no coinciden'
    },
    message: 'Las contraseñas no coinciden'
  }),

  /**
   * Valida que un valor esté dentro de un conjunto de valores permitidos
   * @param validValues Array de valores válidos
   * @param message Mensaje personalizado opcional
   */
  oneOf: (validValues: any[], message?: string): ValidationRule => ({
    validator: (value: any) => {
      return validValues.includes(value) ||
        (message || `El valor debe ser uno de: ${validValues.join(', ')}`);
    }
  }),

  /**
   * Validación condicional genérica: valida un valor según una condición definida
   * @param condition Función que define la condición que debe cumplir el valor
   * @param message Mensaje de error personalizado
   *
   * @example No igual a un valor específico
   * ```typescript
   * // El denominador no puede ser cero
   * validators.condition(value => value !== 0, "No se puede dividir por cero")
   * ```
   *
   * @example No dentro de una lista de valores
   * ```typescript
   * // El estado no puede ser ninguno de los valores cancelados
   * validators.condition(
   *   value => !['CANCELADO', 'ANULADO', 'RECHAZADO'].includes(value),
   *   "Este estado no permite modificaciones"
   * )
   * ```
   *
   * @example Mayor que un valor
   * ```typescript
   * // La cantidad debe ser positiva
   * validators.condition(value => value > 0, "La cantidad debe ser mayor que cero")
   * ```
   *
   * @example Combinando múltiples condiciones
   * ```typescript
   * // La edad debe estar entre 18 y 65
   * validators.condition(
   *   value => value >= 18 && value <= 65,
   *   "La edad debe estar entre 18 y 65 años"
   * )
   * ```
   */
  condition: (condition: (value: any) => boolean, message: string): ValidationRule => ({
    validator: (value: any) => {
      // Si no hay valor, no aplicamos esta validación
      if (value === null || value === undefined || value === '') return true;

      return condition(value) || message;
    }
  }),

  // Métodos específicos que ahora usan el método condition internamente

  /**
   * Valida que un valor NO sea igual a un valor prohibido
   * @param forbiddenValue Valor prohibido
   * @param message Mensaje personalizado opcional
   */
  notEquals: (forbiddenValue: any, message?: string): ValidationRule =>
    validators.condition(
      value => value !== forbiddenValue,
      message || `El valor no puede ser ${forbiddenValue}`
    ),

  /**
   * Valida que un valor NO esté dentro de un conjunto de valores prohibidos
   * @param forbiddenValues Array de valores prohibidos
   * @param message Mensaje personalizado opcional
   */
  notOneOf: (forbiddenValues: any[], message?: string): ValidationRule =>
    validators.condition(
      value => !forbiddenValues.includes(value),
      message || `El valor no puede ser ninguno de estos: ${forbiddenValues.join(', ')}`
    ),

  /**
   * Valida que un número no sea cero (útil para divisiones)
   * @param message Mensaje personalizado opcional
   */
  notZero: (message?: string): ValidationRule =>
    validators.condition(
      value => Number(value) !== 0,
      message || "El valor no puede ser cero"
    ),

  /**
   * Valida que un número sea mayor que un valor mínimo
   * @param min Valor mínimo (exclusivo)
   * @param message Mensaje personalizado opcional
   */
  greaterThan: (min: number, message?: string): ValidationRule =>
    validators.condition(
      value => {
        const numValue = Number(value);
        return !isNaN(numValue) && numValue > min;
      },
      message || `El valor debe ser mayor que ${min}`
    ),

  /**
   * Valida que un número sea menor que un valor máximo
   * @param max Valor máximo (exclusivo)
   * @param message Mensaje personalizado opcional
   */
  lessThan: (max: number, message?: string): ValidationRule =>
    validators.condition(
      value => {
        const numValue = Number(value);
        return !isNaN(numValue) && numValue < max;
      },
      message || `El valor debe ser menor que ${max}`
    ),

  /**
   * Valida que un número esté dentro de un rango (inclusivo)
   * @param min Valor mínimo
   * @param max Valor máximo
   * @param message Mensaje personalizado opcional
   */
  range: (min: number, max: number, message?: string): ValidationRule =>
    validators.condition(
      value => {
        const numValue = Number(value);
        return !isNaN(numValue) && numValue >= min && numValue <= max;
      },
      message || `El valor debe estar entre ${min} y ${max}`
    ),

  /**
   * Validación requerida condicional basada en el valor de otro campo del mismo objeto
   * @param getRelatedValue Función que obtiene el valor del campo relacionado
   * @param condition Función que define cuándo el campo es requerido
   * @param message Mensaje personalizado opcional
   * @param getContext Función opcional que proporciona contexto adicional
   *
   * @example Ejemplo 1: CUPS requerido solo si el servicio NO es 'LABORATORIO'
   * ```typescript
   * validators.requiredIfRelated(
   *   (item, context) => {
   *     const servicios = context?.servicios || []
   *     const servicio = servicios.find(s => s.coseId === item.coseId)
   *     return servicio?.coseNombre
   *   },
   *   (servicioNombre) => servicioNombre !== 'LABORATORIO',
   *   'CUPS es requerido para este tipo de servicio',
   *   () => ({ servicios: tipos.value?.servicios })
   * )
   * ```
   *
   * @example Ejemplo 2: Fecha de vencimiento requerida solo para medicamentos
   * ```typescript
   * validators.requiredIfRelated(
   *   (item) => item.tipoProducto,
   *   (tipo) => tipo === 'MEDICAMENTO',
   *   'La fecha de vencimiento es obligatoria para medicamentos'
   * )
   * ```
   *
   * @example Ejemplo 3: Número de embarazos requerido solo para mujeres mayores de 15 años
   * ```typescript
   * validators.requiredIfRelated(
   *   (item) => ({ genero: item.genero, edad: item.edad }),
   *   (data) => data.genero === 'F' && data.edad >= 15,
   *   'Este campo es requerido para mujeres en edad fértil'
   * )
   * ```
   */
  requiredIfRelated: (
    getRelatedValue: (currentItem: any, context?: any) => any,
    condition: (relatedValue: any) => boolean,
    message?: string,
    getContext?: () => any
  ): ValidationRule => ({
    validator: (value: any, currentItem?: any) => {
      // Si no hay item actual, no podemos validar la relación
      if (!currentItem) return true;

      // Obtener el contexto adicional si se proporciona
      const context = getContext ? getContext() : undefined;
      
      // Obtener el valor relacionado
      const relatedValue = getRelatedValue(currentItem, context);
      
      // Verificar si se cumple la condición que hace el campo requerido
      const isRequired = condition(relatedValue);
      
      // Si no es requerido, la validación pasa
      if (!isRequired) return true;
      
      // Si es requerido, aplicar validación de requerido
      return validators.requerido(value);
    },
    message: message || 'Este campo es requerido bajo las condiciones especificadas'
  })
}

// Exportar como default también para facilitar las importaciones
export default validators
